import React, { useRef, useEffect, useLayoutEffect, useMemo, useState, useCallback } from "react";
import "./ModDropDown.style.scss";

const ModDropDown = ({
    options = [],
    fieldValue = "value",
    field = "label",
    label = "Select an option",
    fieldName,
    percent = "20%",
    background = "#fff",
    onSelect,
    value,
    isOpen,
    onDropdownToggle,
}) => {
    const dropdownRef = useRef(null);
    const itemRefs = useRef([]);
    const typingTimeoutRef = useRef(null);

    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [, setTypedChars] = useState("");
    const [menuPlacement, setMenuPlacement] = useState("down");
    const [menuMaxHeight, setMenuMaxHeight] = useState(250);
    const hasSelectedValue = value !== "-" && value !== "" && value !== null && value !== undefined;
    const dropdownOptions = useMemo(() => Array.isArray(options) ? options : [], [options]);

    const normalizeOptionText = useCallback((text) => {
        return String(text || "")
            .toLowerCase()
            .replace(/\s+/g, " ")
            .trim();
    }, []);

    const normalizeSearchText = useCallback((text) => {
        return String(text || "")
            .toLowerCase()
            .replace(/\s+/g, " ")
            .replace(/^\s+/, "");
    }, []);

    const selectedLabel = useMemo(() => {
        const matchedOption = dropdownOptions.find((option) => option?.[fieldValue] === value);
        return matchedOption ? matchedOption[field] : label;
    }, [value, dropdownOptions, fieldValue, field, label]);

    const scrollToIndex = useCallback((index) => {
        if (index < 0 || !itemRefs.current[index] || !dropdownRef.current) return;

        const dropdown = dropdownRef.current.querySelector(".mod-dropdown-borderBox");
        const item = itemRefs.current[index];

        if (dropdown && item) {
            const dropdownHeight = dropdown.clientHeight;
            const itemTop = item.offsetTop;
            const itemHeight = item.offsetHeight;
            const scrollTo = itemTop - (dropdownHeight / 2) + (itemHeight / 2);
            dropdown.scrollTop = scrollTo;
        }
    }, []);

    const updateMenuPlacement = useCallback(() => {
        if (!dropdownRef.current || typeof window === "undefined") return;

        const viewportPadding = 8;
        const itemHeight = 30;
        const menuChromeHeight = 18;
        const menuMaxVisibleHeight = 288;
        const estimatedMenuHeight = Math.min(
            menuMaxVisibleHeight,
            Math.max(120, (dropdownOptions.length * itemHeight) + menuChromeHeight)
        );

        const rect = dropdownRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
        const spaceAbove = rect.top - viewportPadding;
        const nextPlacement = spaceBelow < estimatedMenuHeight && spaceAbove > spaceBelow
            ? "up"
            : "down";
        const availableSpace = nextPlacement === "up" ? spaceAbove : spaceBelow;

        setMenuPlacement(nextPlacement);
        setMenuMaxHeight(Math.max(120, Math.min(menuMaxVisibleHeight, availableSpace)));
    }, [dropdownOptions.length]);

    const handleSelect = useCallback((selectedValue) => {
        if (onSelect) onSelect(fieldName, selectedValue);
        if (onDropdownToggle) onDropdownToggle(fieldName, selectedValue, false);
        setTypedChars("");
        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
        }
    }, [onSelect, onDropdownToggle, fieldName]);

    const handleToggleDropdown = (event) => {
        event.stopPropagation();

        if (onDropdownToggle) {
            onDropdownToggle(fieldName, value, !isOpen);
        }

        if (!isOpen) {
            updateMenuPlacement();

            const selectedIndex = dropdownOptions.findIndex((option) => option?.[fieldValue] === value);
            const nextIndex = selectedIndex >= 0 ? selectedIndex : -1;

            setHighlightedIndex(nextIndex);
            setTypedChars("");

            setTimeout(() => {
                if (nextIndex >= 0) {
                    scrollToIndex(nextIndex);
                }
            }, 0);
        }
    };

    const handleReset = () => {
        if (onSelect) onSelect(fieldName, "-");
        if (onDropdownToggle) onDropdownToggle(fieldName, "-", false);
        setTypedChars("");
        setHighlightedIndex(-1);

        if (typingTimeoutRef.current) {
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = null;
        }
    };

    const handleTypeAhead = useCallback((char) => {
        setTypedChars((prevTypedChars) => {
            const nextTypedChars = normalizeSearchText(prevTypedChars + char);

            const matchingIndex = dropdownOptions.findIndex((option) =>
                normalizeOptionText(option?.[field]).startsWith(nextTypedChars)
            );

            if (matchingIndex >= 0) {
                setHighlightedIndex(matchingIndex);
            }

            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }

            typingTimeoutRef.current = setTimeout(() => {
                setTypedChars("");
            }, 1000);

            return nextTypedChars;
        });
    }, [dropdownOptions, field, normalizeOptionText, normalizeSearchText]);

    const handleKeyDown = useCallback((event) => {
        if (!isOpen) return;

        const totalOptions = dropdownOptions.length;

        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                setHighlightedIndex((prev) => Math.min(prev + 1, totalOptions - 1));
                break;

            case "ArrowUp":
                event.preventDefault();
                setHighlightedIndex((prev) => Math.max(prev - 1, 0));
                break;

            case "Enter":
                event.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < totalOptions) {
                    const selectedOption = dropdownOptions[highlightedIndex];
                    handleSelect(selectedOption?.[fieldValue]);
                }
                break;

            case "Escape":
                event.preventDefault();
                if (onDropdownToggle) onDropdownToggle(fieldName, value, false);
                break;

            case "Backspace":
                event.preventDefault();
                setTypedChars((prevTypedChars) => {
                    const nextTypedChars = prevTypedChars.slice(0, -1);

                    if (!nextTypedChars) {
                        const selectedIndex = dropdownOptions.findIndex(
                            (option) => option?.[fieldValue] === value
                        );
                        setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : -1);
                    } else {
                        const matchingIndex = dropdownOptions.findIndex((option) =>
                            normalizeOptionText(option?.[field]).startsWith(nextTypedChars)
                        );
                        if (matchingIndex >= 0) {
                            setHighlightedIndex(matchingIndex);
                        }
                    }

                    if (typingTimeoutRef.current) {
                        clearTimeout(typingTimeoutRef.current);
                    }

                    typingTimeoutRef.current = setTimeout(() => {
                        setTypedChars("");
                    }, 1000);

                    return nextTypedChars;
                });
                break;

            default:
                if (
                    event.key.length === 1 &&
                    !event.ctrlKey &&
                    !event.metaKey &&
                    !event.altKey
                ) {
                    event.preventDefault();
                    handleTypeAhead(event.key);
                }
                break;
        }
    }, [
        isOpen,
        dropdownOptions,
        highlightedIndex,
        handleSelect,
        onDropdownToggle,
        fieldName,
        value,
        fieldValue,
        field,
        handleTypeAhead,
        normalizeOptionText,
    ]);

    useEffect(() => {
        if (highlightedIndex >= 0) {
            scrollToIndex(highlightedIndex);
        }
    }, [highlightedIndex, scrollToIndex]);

    useLayoutEffect(() => {
        if (isOpen) {
            updateMenuPlacement();
        }
    }, [isOpen, updateMenuPlacement]);

    useEffect(() => {
        if (!isOpen) return undefined;

        window.addEventListener("resize", updateMenuPlacement);
        window.addEventListener("scroll", updateMenuPlacement, true);

        return () => {
            window.removeEventListener("resize", updateMenuPlacement);
            window.removeEventListener("scroll", updateMenuPlacement, true);
        };
    }, [isOpen, updateMenuPlacement]);

    useEffect(() => {
        if (!isOpen) return undefined;

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen, handleKeyDown]);

    useEffect(() => {
        return () => {
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div
            className="mod-dropdown"
            style={{ width: percent, display: "flex", alignItems: "center" }}
            ref={dropdownRef}
        >
            <button
                className={`mod-dropdown-button ${hasSelectedValue ? "has-selected-value" : ""}`}
                onClick={handleToggleDropdown}
                style={{
                    height: "33px",
                    lineHeight: "33px",
                    position: "relative",
                    backgroundColor: hasSelectedValue ? "black" : background,
                    color: hasSelectedValue ? "yellow" : "inherit",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "start",
                    boxSizing: "border-box",
                }}
                title={selectedLabel}
                type="button"
            >
                {selectedLabel}
            </button>

            <button
                className="reset-button"
                onClick={handleReset}
                disabled={!hasSelectedValue}
                style={{
                    marginLeft: "0px",
                    width: "33px",
                    height: "33px",
                    lineHeight: "33px",
                    backgroundColor: hasSelectedValue ? "black" : background,
                    color: hasSelectedValue ? "yellow" : "lightGrey",
                    border: "1px solid #ced4da",
                    borderLeft: "none",
                    borderRadius: "0px 4px 4px 0px",
                    cursor: hasSelectedValue ? "pointer" : "default",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
                type="button"
            >
                {hasSelectedValue ? "X" : ""}
            </button>

            {isOpen && (
                <div
                    className={`mod-dropdown-borderBox ${
                        menuPlacement === "up" ? "opens-up" : "opens-down"
                    }`}
                    style={{ maxHeight: `${menuMaxHeight}px` }}
                >
                    {dropdownOptions.map((option, index) => (
                        <button
                            key={index}
                            ref={(el) => {
                                itemRefs.current[index] = el;
                            }}
                            className={`mod-dropdown-menu-item ${
                                index === highlightedIndex ? "highlighted" : ""
                            }`}
                            onClick={() => handleSelect(option?.[fieldValue])}
                            type="button"
                        >
                            {option?.[field]}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ModDropDown;
